import noop from 'lodash/noop';
import React, { FC, Suspense, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { errorsToErrorSummary } from '../../../../utils/form';
import { Button } from '../../../shared/Button';
import { Paragraph } from '../../../shared/Paragraph';
import { PopUpHandler } from '../../../shared/PopupHandler';
import { Select } from '../../../shared/Select';
import { Spinner } from '../../../shared/Spinner';
import './AccountReviewEditModal.scss';
import { getAccountReviewOptions } from '../mocks';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';

interface AccountReviewEditModalProps {
  loading: boolean;
  isOpen: boolean;
  intervalWithLabel: string;
  onClose: () => void;
  onFormSubmit: (data: AccountReviewFormData) => void;
  onShow?: () => void;
  onCancel?: () => void;
}

export interface AccountReviewFormData {
  interval: string;
}

export const AccountReviewEditModal: FC<AccountReviewEditModalProps> = ({
  isOpen,
  loading,
  intervalWithLabel,
  onClose,
  onFormSubmit,
  onShow = noop,
  onCancel = noop,
}) => {
  const { t } = useTranslations();
  const { control, formState, reset, handleSubmit } = useForm<AccountReviewFormData>({
    defaultValues: {
      interval: intervalWithLabel,
    },
  });

  const errorSummary = errorsToErrorSummary(formState.errors, {}, 'account-review-form');

  async function onSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    if (!formState.isSubmitting) {
      return handleSubmit(({ interval }) => onFormSubmit({ interval: getValueFromLabel(interval) }))(event);
    }
  }

  const getValueFromLabel = (label: string): string => {
    const options = getAccountReviewOptions(t);
    const option = options.find((opt) => opt.label === label);

    return option?.value || label;
  };

  const getLabelFromValue = (value: string): string => {
    const options = getAccountReviewOptions(t);
    const option = options.find((opt) => opt.value === value);

    return option?.label || value;
  };

  useEffect(() => {
    if (isOpen) {
      onShow();
    }
  }, [isOpen]);

  useEffect(() => {
    reset({ interval: getLabelFromValue(intervalWithLabel) });
  }, [intervalWithLabel]);

  return (
    <PopUpHandler
      isOpen={isOpen}
      onClose={onClose}
      header={t('account-review.play-reminders')}
      drawerProps={{ backdropCanClose: true }}
      headingProps={{ headingLevel: 5 }}
      modalProps={{
        className: 'account-review-edit-modal',
        'aria-label': 'Play Reminders',
      }}
      footer={
        <div className="account-review-edit-modal__action-footer">
          <Button
            className="account-review-edit-modal__cancel-btn"
            variant="secondary-alt"
            type="button"
            onClick={onCancel}
            disabled={loading}
          >
            {t('buttons.cancel.uppercase')}
          </Button>
          <Button variant="primary" type="submit" form="account-review-form" disabled={loading}>
            {t('buttons.confirm.uppercase')}
          </Button>
        </div>
      }
    >
      <form onSubmit={onSubmit} className="account-review-edit-modal__select-form" id="account-review-form">
        <Paragraph size="sm" noMargin className="account-review-edit-modal__select-text">
          {t('account-review.main-text-1')}
        </Paragraph>
        <Paragraph size="sm" noMargin className="account-review-edit-modal__select-text">
          {t('account-review.main-text-2')}
        </Paragraph>
        {loading ? (
          <Spinner />
        ) : (
          <Suspense>
            <Controller
              control={control}
              name="interval"
              defaultValue={intervalWithLabel}
              rules={{
                required: {
                  value: true,
                  message: t('validation.required'),
                },
                validate: (value: string): string | undefined => {
                  return value !== intervalWithLabel ? undefined : t('account-review.existed-interval-message');
                },
              }}
              render={({ field, ...renderProps }): JSX.Element => {
                const opts = getAccountReviewOptions(t);
                const valueToLabel = (v: string): string => opts.find((o) => o.value === v)?.label ?? v;

                return (
                  <Select
                    {...renderProps}
                    {...field}
                    onChange={(v: string): void => field.onChange(valueToLabel(v))}
                    className="account-review-edit-modal__select"
                    label={t('account-review.time-limit')}
                    placement="bottom"
                    theme="dark"
                    options={opts}
                    validationMessages={errorSummary.interval?.messages}
                    validationState={errorSummary.interval?.messages?.length ? 'error' : undefined}
                  />
                );
              }}
            />
          </Suspense>
        )}
      </form>
    </PopUpHandler>
  );
};
